/**
 * Per-locale title + lede overrides for notes. Bodies stay EN-canonical
 * for now — translating long-form essays well takes more bandwidth than
 * we have, and a half-translated body reads worse than an EN-marked one.
 *
 * If a locale entry is missing, callers fall back to the EN field on the
 * Note itself. Marker on localized index/page tells the reader the body
 * is the original English.
 */

import type { Locale } from "@/lib/i18n";

type LocalizedFields = {
  title: string;
  lede: string;
};

type Localizations = Record<string, Partial<Record<Locale, LocalizedFields>>>;

export const NOTE_LOCALIZATIONS: Localizations = {
  "portfolio-data-rots": {
    sq: {
      title: "Portofoli po gënjente për ne.",
      lede:
        "E drejtuam stack-un e agjentëve te sajti ynë dhe audituam çdo pohim kundrejt sajteve live të të nëntë produkteve. Nuk doli i pastër — dhe zgjidhja nuk ishte copywriting, ishte arkitekturë.",
    },
    ar: {
      title: "كانت صفحة الأعمال تكذب بشأننا.",
      lede:
        "وجّهنا منظومة الوكلاء نحو موقعنا وراجعنا كل ادعاء مقابل المواقع الحيّة للمنتجات التسعة. لم تخرج المراجعة نظيفة — والحل لم يكن في الصياغة، بل في البنية.",
    },
  },
  "progressive-enhancement-webgl-seo": {
    sq: {
      title: "Nëse kanvasi është faqja jote kryesore, nuk ke faqe kryesore.",
      lede:
        "Si ta bësh një përvojë WebGL me rrëshqitje të indeksueshme pa hequr kanvasin — dhe pse faqja jote e uljes nuk duhet të ketë nevojë për JavaScript që të ekzistojë.",
    },
    ar: {
      title: "إذا كان الكانفاس هو صفحتك الرئيسية، فأنت بلا صفحة رئيسية.",
      lede:
        "كيف تجعل تجربة WebGL التمريرية قابلة للفهرسة دون إزالة الكانفاس — ولماذا لا ينبغي لصفحتك الرئيسية أن تحتاج جافاسكربت لكي توجد.",
    },
  },
  "fourteen-days": {
    sq: {
      title: "Katërmbëdhjetë ditë, nga brifi te staging.",
      lede:
        "Pse ritmi i studios nuk është thjesht slogan — dhe çfarë heqim që ta bëjmë reale.",
    },
    ar: {
      title: "أربعة عشر يوماً، من الموجز إلى staging.",
      lede:
        "لماذا إيقاع الستوديو ليس مجرد عبارة تسويقية — وما الذي نحذفه لجعله حقيقياً.",
    },
  },
  "trilingual-from-day-one": {
    sq: {
      title: "Trigjuhësh nga dita e parë — dhe sa kushton vërtet.",
      lede:
        "Pse ndërtimi EN / SQ / AR-native nuk është thjesht përkthim — dhe katër vendet ku shfaqet në build.",
    },
    ar: {
      title: "ثلاثي اللغة منذ اليوم الأول — وما يكلّفه فعلاً.",
      lede:
        "لماذا البناء بثلاث لغات أصيلة (EN / SQ / AR) ليس عملية ترجمة — وأربعة مواضع يظهر فيها داخل الـ build.",
    },
  },
  "agent-stack-shipped-this": {
    sq: {
      title: "Stiva e agjentëve që dorëzoi këtë sajt.",
      lede:
        "Cilët agjentë prekën cilët skedarë — dhe çfarë studio ende e bën me dorë.",
    },
    ar: {
      title: "حزمة الوكلاء التي أطلقت هذا الموقع.",
      lede:
        "أيّ وكيل لمس أيّ ملف — وماذا لا يزال الستوديو يفعله يدوياً.",
    },
  },
  "wise-not-stripe-for-studio-billing": {
    sq: {
      title: "Pse e zhvendosëm faturimin e studios te Wise.",
      lede:
        "Stripe është perfekt për produkt. Nuk është mjeti i duhur për fatura studio që mbulojnë katër monedha.",
    },
    ar: {
      title: "لماذا حوّلنا فوترة الستوديو إلى Wise.",
      lede:
        "Stripe ممتاز للمنتجات. ليس الأداة المناسبة لفواتير ستوديو تشمل أربع عملات.",
    },
  },
};

export function localizedNoteFields(slug: string, locale: Locale): LocalizedFields | undefined {
  if (locale === "en") return undefined;
  return NOTE_LOCALIZATIONS[slug]?.[locale];
}
